/**
 * PDF Service - Server-side PDF generation
 * Uses WeasyPrint on the backend for professional quality PDFs
 * with proper page breaks, margins, and text handling
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Download CV as PDF (server-generated)
 * @param {string} profileId - The profile ID
 * @param {string} language - Language code (fr/en)
 * @param {string} firstName - For filename
 * @param {string} lastName - For filename
 */
export async function downloadCVPdf(profileId, language = 'fr', firstName = 'CV', lastName = '') {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/pdf/cv/${profileId}?language=${language}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'PDF generation failed' }));
      throw new Error(error.detail || 'Failed to generate PDF');
    }

    // Get the blob
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `CV_${firstName}_${lastName}.pdf`.replace(/\s+/g, '_').replace(/_+/g, '_');
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('CV PDF download error:', error);
    throw error;
  }
}

/**
 * Download Personality Report as PDF (server-generated)
 * @param {string} profileId - The profile ID
 * @param {string} language - Language code (fr/en)
 * @param {string} firstName - For filename
 * @param {string} lastName - For filename
 */
export async function downloadPersonalityPdf(profileId, language = 'fr', firstName = 'Personality', lastName = '') {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/pdf/personality/${profileId}?language=${language}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'PDF generation failed' }));
      throw new Error(error.detail || 'Failed to generate PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Personality_Report_${firstName}_${lastName}.pdf`.replace(/\s+/g, '_').replace(/_+/g, '_');
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Personality PDF download error:', error);
    throw error;
  }
}

/**
 * Download CV Feedback Report as PDF (server-generated)
 * @param {Object} feedback - The feedback data
 * @param {string} language - Language code (fr/en)
 */
export async function downloadFeedbackPdf(feedback, language = 'fr') {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/pdf/feedback?language=${language}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
        },
        body: JSON.stringify(feedback),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'PDF generation failed' }));
      throw new Error(error.detail || 'Failed to generate PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'CV_Feedback_Report.pdf';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Feedback PDF download error:', error);
    throw error;
  }
}

/**
 * Download Complete Profile as PDF (server-generated)
 * Includes CV and Personality Report if available
 * @param {string} profileId - The profile ID
 * @param {string} language - Language code (fr/en)
 * @param {string} firstName - For filename
 * @param {string} lastName - For filename
 */
export async function downloadCompleteProfilePdf(profileId, language = 'fr', firstName = 'Profile', lastName = '') {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/pdf/complete/${profileId}?language=${language}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'PDF generation failed' }));
      throw new Error(error.detail || 'Failed to generate PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Complete_Profile_${firstName}_${lastName}.pdf`.replace(/\s+/g, '_').replace(/_+/g, '_');
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Complete Profile PDF download error:', error);
    throw error;
  }
}

/**
 * Check if server-side PDF generation is available
 * Falls back to client-side if not
 */
export async function checkPdfServiceAvailable() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/health`);
    return response.ok;
  } catch {
    return false;
  }
}

export default {
  downloadCVPdf,
  downloadPersonalityPdf,
  downloadFeedbackPdf,
  downloadCompleteProfilePdf,
  checkPdfServiceAvailable,
};
